import {
  Box,
  createListCollection,
  Flex,
  Input,
  type ListCollection,
  Portal,
  Select as ChakraSelect,
  Text,
} from '@chakra-ui/react';
import { Search } from 'lucide-react';
import * as React from 'react';

import {
  menuContentGapSm,
  menuContentMinWidth,
  menuContentPaddingLg,
  menuItemBorderRadius,
  menuItemMinHeight,
  menuSearchIconColor,
  menuSearchIconSize,
  menuSearchIconStrokeWidth,
  menuSearchPaddingY,
  menuSearchTextColor,
} from '../menu/menu.kds';
import { Select } from '../select';

export interface SelectTableCellItem {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface SelectTableCellProps {
  /** Simple items to display. Strings are used as both label and value. */
  items?: Array<string | SelectTableCellItem>;
  /** Pre-built collection (use instead of `items` for complex cases). */
  collection?: ListCollection;
  /** Placeholder text shown when no value is selected. */
  placeholder?: string;
  /** Controlled selected value. */
  value?: string;
  /** Called when the selection changes. */
  onValueChange?: (value: string) => void;
  /** Disables the cell. */
  disabled?: boolean;
  /** Name attribute for form submission. */
  name?: string;
  /** Custom dropdown content (replaces default items list). */
  children?: React.ReactNode;
  /** Enable search input in the dropdown. */
  searchable?: boolean;
  /** Controlled search value (requires `searchable`). */
  searchValue?: string;
  /** Called when the search input value changes. */
  onSearchChange?: (value: string) => void;
  /** Placeholder for the search input. */
  searchPlaceholder?: string;
  /** Minimum width for the dropdown content (e.g. "280px"). Useful when the trigger cell is narrow. */
  minDropdownWidth?: string;
  /** Optional aria-label for accessibility. */
  'aria-label'?: string;
  /** Optional CSS overrides. */
  css?: Record<string, unknown>;
  /** Called when the dropdown opens or closes. Use to style the parent cell. */
  onOpenChange?: (open: boolean) => void;
}

/** CSS to make the Select control/trigger fully transparent so the parent td drives visuals. */
const transparentTriggerCss = {
  h: '100%',
  border: 'none',
  borderRadius: 0,
  bg: 'transparent',
  shadow: 'none',
  outline: 'none',
  px: '3',
  _hover: { bg: 'transparent', borderColor: 'transparent' },
  '&:has(:focus-visible)': { borderWidth: 0, borderColor: 'transparent', outline: 'none', bg: 'transparent' },
  '&:has([aria-expanded=true])': { borderColor: 'transparent', bg: 'transparent' },
  '&:has(:disabled)': { bg: 'transparent', borderColor: 'transparent', opacity: 1 },
  '&:has([aria-invalid=true])': { borderColor: 'transparent' },
} as const;

function stopPropagation<T extends React.SyntheticEvent>(handler?: (event: T) => void) {
  return (event: T) => {
    event.stopPropagation();
    handler?.(event);
  };
}

const SelectTableCellContent = React.forwardRef<
  HTMLDivElement,
  {
    searchable?: boolean;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    searchPlaceholder?: string;
    minDropdownWidth?: string;
    children: React.ReactNode;
  }
>(function SelectTableCellContent(props, ref) {
  const {
    searchable,
    searchValue,
    onSearchChange,
    searchPlaceholder = 'Search',
    minDropdownWidth,
    children,
  } = props;

  return (
    <Portal>
      <ChakraSelect.Positioner>
        <ChakraSelect.Content
          ref={ref}
          css={{
            p: 0,
            minWidth: minDropdownWidth ?? menuContentMinWidth,
            bg: 'neutral.0',
            borderWidth: '1px',
            borderColor: 'neutral.100',
            borderRadius: menuItemBorderRadius,
            boxShadow: 'menu',
            overflow: 'clip',
          }}
        >
          {searchable && (
            <Flex
              align="center"
              borderBottomWidth="1px"
              borderColor="neutral.100"
              gap={menuContentGapSm}
              px={menuContentPaddingLg}
              py={menuSearchPaddingY}
              onPointerDown={stopPropagation()}
              onClick={stopPropagation()}
            >
              <Box color={menuSearchIconColor} flexShrink={0} lineHeight={0}>
                <Search aria-hidden size={menuSearchIconSize} strokeWidth={menuSearchIconStrokeWidth} />
              </Box>
              <Input
                unstyled
                bg="transparent"
                border="none"
                color={menuSearchTextColor}
                flex="1"
                minW={0}
                outline="none"
                placeholder={searchPlaceholder}
                textStyle="default.regular"
                type="search"
                value={searchValue ?? ''}
                _placeholder={{ color: menuSearchIconColor }}
                onChange={e => onSearchChange?.(e.target.value)}
                onPointerDown={stopPropagation()}
                onClick={stopPropagation()}
                onKeyDown={e => {
                  if (e.key === ' ') e.stopPropagation();
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
            </Flex>
          )}
          <Box
            px={menuContentPaddingLg}
            pt={menuContentGapSm}
            pb={menuContentPaddingLg}
            maxH="300px"
            overflowY="auto"
          >
            <ChakraSelect.List
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: menuContentGapSm,
                '& [role="option"]': {
                  minH: menuItemMinHeight,
                  p: menuContentGapSm,
                  borderRadius: menuItemBorderRadius,
                  gap: menuContentGapSm,
                  justifyContent: 'flex-start',
                  textStyle: 'default.regular',
                  color: 'neutral.500',
                  _highlighted: {
                    bg: 'brand.50',
                    color: 'neutral.500',
                  },
                  '&[data-state=checked]': {
                    bg: 'brand.100',
                    color: 'brand.500',
                  },
                },
              }}
            >
              {children}
            </ChakraSelect.List>
          </Box>
        </ChakraSelect.Content>
      </ChakraSelect.Positioner>
    </Portal>
  );
});

export const SelectTableCell = React.forwardRef<HTMLDivElement, SelectTableCellProps>(
  function SelectTableCell(props, ref) {
    const {
      items,
      collection: collectionProp,
      placeholder = 'Select',
      value = '',
      onValueChange,
      disabled,
      name,
      children,
      searchable,
      searchValue,
      onSearchChange,
      searchPlaceholder,
      minDropdownWidth,
      'aria-label': ariaLabel,
      css: cssProp,
      onOpenChange: onOpenChangeProp,
    } = props;

    const [internalSearch, setInternalSearch] = React.useState('');
    const effectiveSearchValue = searchValue ?? internalSearch;
    const effectiveOnSearchChange = onSearchChange ?? setInternalSearch;

    const cellRef = React.useRef<HTMLTableCellElement | null>(null);
    const rootRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
        cellRef.current = node?.closest('td') ?? null;
      },
      [ref]
    );

    const getAnchorRect = React.useCallback(() => {
      const td = cellRef.current;
      if (!td) return null;
      return td.getBoundingClientRect();
    }, []);

    const builtCollection = React.useMemo(() => {
      if (collectionProp) return collectionProp;
      if (!items) return createListCollection({ items: [] as SelectTableCellItem[] });
      const normalized = items.map(item =>
        typeof item === 'string' ? { label: item, value: item } : item
      );
      return createListCollection({
        items: normalized,
        itemToString: i => i.label,
        itemToValue: i => i.value,
      });
    }, [items, collectionProp]);

    const handleOpenChange = React.useCallback(
      ({ open }: { open: boolean }) => {
        onOpenChangeProp?.(open);
      },
      [onOpenChangeProp]
    );

    return (
      <Select.Root
        ref={rootRef}
        collection={builtCollection}
        value={value ? [value] : []}
        onValueChange={({ value: v }) => onValueChange?.(v[0] ?? '')}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        name={name}
        size="lg"
        positioning={{ sameWidth: false, placement: 'bottom-start', offset: { mainAxis: 4 }, getAnchorRect }}
        aria-label={ariaLabel}
        css={{ w: '100%', ...cssProp }}
      >
        <Select.Trigger css={transparentTriggerCss} style={{ fontSize: 14, fontWeight: 400 }}>
          <Select.ValueText placeholder={placeholder} />
        </Select.Trigger>
        <SelectTableCellContent
          searchable={searchable}
          searchValue={effectiveSearchValue}
          onSearchChange={effectiveOnSearchChange}
          searchPlaceholder={searchPlaceholder}
          minDropdownWidth={minDropdownWidth}
        >
          {children ??
            builtCollection.items.map((item: SelectTableCellItem) => (
              <Select.Item key={item.value} item={item}>
                <Text>{item.label}</Text>
              </Select.Item>
            ))}
        </SelectTableCellContent>
      </Select.Root>
    );
  }
);

SelectTableCell.displayName = 'SelectTableCell';

function SelectTableCellSectionLabel({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <Text
      textStyle="small.medium"
      color="neutral.350"
      textTransform="uppercase"
      p={menuContentGapSm}
      userSelect="none"
    >
      {children}
    </Text>
  );
}
SelectTableCellSectionLabel.displayName = 'SelectTableCellSectionLabel';

export { SelectTableCellSectionLabel };
